import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';

import { useLang } from '../../context/LangContext';
import enTexts from '../../translations/en.json';
import esTexts from '../../translations/es.json';

function FiltersHome({
  onCountrySelected,
  onRegionSelected,
  onCiudadSelected,
  accommodations = null,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [regionsOptions, setRegionsOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const { lang } = useLang();
  const [texts, setTexts] = useState(lang === 'es' ? esTexts : enTexts);

  const toggleMenu = () => {
    const willOpen = !showMenu;
    setShowMenu(willOpen);

    // Solo se debe filtrar y auto-seleccionar al "abrir" la sección de filtros si hay una única opción
    if (willOpen && !selectedCountry && countriesOptions.length === 1) {
      const onlyCountry = countriesOptions[0];
      setSelectedCountry(onlyCountry);
      const availableRegions = onlyCountry.regions || [];
      setRegionsOptions(availableRegions);
      if (onCountrySelected) onCountrySelected(Number(onlyCountry.value));

      if (availableRegions.length === 1) {
        const onlyRegion = availableRegions[0];
        setSelectedRegion(onlyRegion);
        const availableCities = onlyRegion.cities || [];
        setCitiesOptions(availableCities);
        if (onRegionSelected) onRegionSelected(onlyRegion.value);

        if (availableCities.length === 1) {
          const onlyCity = availableCities[0];
          setSelectedCity(onlyCity);
          if (onCiudadSelected) onCiudadSelected(onlyCity.value);
        }
      }
    }
  };

  const [allActiveRegions, setAllActiveRegions] = useState([]);

  const isChileRegionActive = regionName => {
    const lower = regionName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return lower.includes('metropolitana') || lower.includes('valparaiso');
  };

  const getRegions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/es/regions?onlyWithAccommodations=true`
      );
      const { regions: data } = response.data;

      // If accommodations prop is passed and has items, filter cities matching current accommodations
      const accommodationCityIds =
        accommodations && Array.isArray(accommodations) && accommodations.length > 0
          ? new Set(accommodations.map(a => a.city_id).filter(Boolean))
          : null;

      const countriesMap = {};
      const activeRegionsList = [];

      data.forEach(region => {
        const isChile =
          !region.country ||
          region.country.abbreviation === 'CL' ||
          region.country.name === 'Chile';
        // Only include RM and V for Chile
        if (isChile && !isChileRegionActive(region.name)) {
          return;
        }

        const filteredCities = region.cities.filter(city => {
          if (accommodationCityIds) {
            return accommodationCityIds.has(city.id);
          }
          return true;
        });

        // If filtering by specific accommodations in view and no cities match, skip region
        if (accommodationCityIds && filteredCities.length === 0) {
          return;
        }

        const regionData = {
          value: region.id,
          label: region.name.includes('Metropolitana')
            ? 'Región Metropolitana (RM)'
            : region.name.includes('Valparaíso') || region.name.includes('Valparaiso')
              ? 'Región de Valparaíso (V)'
              : region.name,
          cities: filteredCities.map(city => ({
            value: city.id,
            label: city.name,
          })),
          subways: region.subways.map(subway => ({
            value: subway.id,
            label: subway.name,
          })),
        };
        activeRegionsList.push(regionData);

        if (region.country) {
          if (!countriesMap[region.country.id]) {
            countriesMap[region.country.id] = {
              value: region.country.id,
              label: region.country.name,
              regions: [],
            };
          }
          countriesMap[region.country.id].regions.push(regionData);
        }
      });

      // Include countries referenced by the loaded accommodations even if they
      // have no region/city in the database (e.g. country-level properties)
      if (accommodations && Array.isArray(accommodations)) {
        accommodations.forEach(acc => {
          if (!acc.country_id) return;
          if (!countriesMap[acc.country_id]) {
            countriesMap[acc.country_id] = {
              value: acc.country_id,
              label: acc.country_name || acc.country_abbreviation,
              regions: [],
            };
          } else if (!countriesMap[acc.country_id].label) {
            countriesMap[acc.country_id].label = acc.country_name || acc.country_abbreviation;
          }
        });
      }

      const availableCountries = Object.values(countriesMap);
      setCountriesOptions(availableCountries);
      setAllActiveRegions(activeRegionsList);
      setRegionsOptions(activeRegionsList);
    } catch (err) {
      console.error('Error fetching regions:', err);
    }
  };

  const handleCountryChange = selectedOption => {
    setSelectedRegion(null);
    setSelectedCity(null);
    if (onRegionSelected) onRegionSelected(null);
    if (onCiudadSelected) onCiudadSelected(null);

    if (!selectedOption) {
      setSelectedCountry(null);
      setRegionsOptions(allActiveRegions);
      setCitiesOptions([]);
      if (onCountrySelected) onCountrySelected(null);
      return;
    }

    setSelectedCountry(selectedOption);
    const availableRegions = selectedOption.regions || [];
    setRegionsOptions(availableRegions);
    if (onCountrySelected) onCountrySelected(Number(selectedOption.value));

    // Si el país seleccionado tiene solo 1 región disponible, auto-seleccionarla
    if (availableRegions.length === 1) {
      const onlyRegion = availableRegions[0];
      setSelectedRegion(onlyRegion);
      const availableCities = onlyRegion.cities || [];
      setCitiesOptions(availableCities);
      if (onRegionSelected) onRegionSelected(onlyRegion.value);

      // Si además esa única región tiene sólo 1 comuna/ciudad, auto-seleccionarla
      if (availableCities.length === 1) {
        const onlyCity = availableCities[0];
        setSelectedCity(onlyCity);
        if (onCiudadSelected) onCiudadSelected(onlyCity.value);
      }
    }
  };

  const handleRegionChange = selectedOption => {
    setSelectedCity(null);
    if (onCiudadSelected) onCiudadSelected(null);

    if (!selectedOption) {
      setSelectedRegion(null);
      setCitiesOptions([]);
      if (onRegionSelected) onRegionSelected(null);
      return;
    }

    setSelectedRegion(selectedOption);
    const availableCities = selectedOption.cities || [];
    setCitiesOptions(availableCities);

    if (onRegionSelected) onRegionSelected(selectedOption.value);

    // Si la región seleccionada tiene solo 1 comuna disponible, auto-seleccionarla
    if (availableCities.length === 1) {
      const onlyCity = availableCities[0];
      setSelectedCity(onlyCity);
      if (onCiudadSelected) onCiudadSelected(onlyCity.value);
    }
  };

  const handleCiudadChange = selectedOption => {
    if (!selectedOption) {
      setSelectedCity(null);
      if (onCiudadSelected) onCiudadSelected(null);
      return;
    }
    setSelectedCity(selectedOption);
    if (onCiudadSelected) onCiudadSelected(selectedOption.value);
  };

  const closeCountry = () => {
    setSelectedCountry(null);
    setRegionsOptions(allActiveRegions);
    if (onCountrySelected) onCountrySelected(null);
    closeRegion();
  };

  const closeRegion = () => {
    setSelectedRegion(null);
    setCitiesOptions([]);
    if (onRegionSelected) onRegionSelected(null);
    closeCity();
  };

  const closeCity = () => {
    setSelectedCity(null);
    if (onCiudadSelected) onCiudadSelected(null);
  };

  const clearAllFilters = () => {
    closeCountry();
  };

  const activeFiltersCount =
    (selectedCountry ? 1 : 0) + (selectedRegion ? 1 : 0) + (selectedCity ? 1 : 0);

  useEffect(() => {
    getRegions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accommodations]);

  useEffect(() => {
    setTexts(lang === 'es' ? esTexts : enTexts);
  }, [lang]);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#f8fafc',
      borderRadius: '0.65rem',
      borderColor: state.isFocused ? '#475569' : '#e2e8f0',
      borderWidth: '1px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(71, 85, 105, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#cbd5e1',
        backgroundColor: '#ffffff',
      },
      fontSize: '0.9rem',
      minHeight: '42px',
      paddingLeft: '2px',
      transition: 'all 0.2s ease',
    }),
    placeholder: base => ({
      ...base,
      color: '#94a3b8',
      fontSize: '0.875rem',
      fontWeight: '400',
    }),
    singleValue: base => ({
      ...base,
      color: '#1e293b',
      fontWeight: '500',
    }),
    menu: base => ({
      ...base,
      borderRadius: '0.65rem',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f1f5f9',
      overflow: 'hidden',
      zIndex: 20,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.88rem',
      backgroundColor: state.isSelected ? '#334155' : state.isFocused ? '#f1f5f9' : 'transparent',
      color: state.isSelected ? '#ffffff' : '#334155',
      cursor: 'pointer',
    }),
  };

  return (
    <div className="filters-container mb-2">
      {/* Filter Trigger Button + Active Chips Bar */}
      <div className="d-flex flex-wrap align-items-center gap-2 py-1">
        <button
          type="button"
          onClick={toggleMenu}
          className={`btn btn-sm d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1-5 border transition-all ${
            showMenu || activeFiltersCount > 0
              ? 'bg-slate-800 text-white border-secondary'
              : 'bg-white text-secondary border-light-subtle shadow-2xs hover-border-secondary'
          }`}
          style={{
            fontSize: '0.85rem',
            backgroundColor: showMenu || activeFiltersCount > 0 ? '#334155' : '#ffffff',
            color: showMenu || activeFiltersCount > 0 ? '#ffffff' : '#475569',
            borderColor: showMenu || activeFiltersCount > 0 ? '#334155' : '#e2e8f0',
            outline: 'none',
            boxShadow: 'none',
          }}
          title={lang === 'es' ? 'Filtrar por ubicación' : 'Filter by location'}
        >
          <i
            className="fa fa-sliders"
            style={{
              fontSize: '0.95rem',
              color: showMenu || activeFiltersCount > 0 ? '#ffffff' : '#64748b',
            }}
          ></i>
          <span className="fw-semibold">{lang === 'es' ? 'Filtros' : 'Filters'}</span>
          {activeFiltersCount > 0 && (
            <span
              className="badge rounded-circle px-2 py-1"
              style={{
                fontSize: '0.7rem',
                backgroundColor: showMenu ? 'rgba(255,255,255,0.25)' : '#475569',
                color: '#ffffff',
              }}
            >
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* Active Filter Chips: Only show when filter panel is collapsed */}
        {!showMenu && (
          <>
            {selectedCountry && (
              <div
                className="d-inline-flex align-items-center gap-2 bg-white border border-secondary-subtle px-3 py-1 rounded-pill shadow-xs"
                style={{ fontSize: '0.825rem' }}
              >
                <i className="fa fa-globe text-secondary"></i>
                <span className="text-secondary small">{lang === 'es' ? 'País:' : 'Country:'}</span>
                <strong className="text-dark">{selectedCountry.label}</strong>
                <i
                  className="fa fa-times text-muted cursor-pointer ms-1 hover-danger"
                  onClick={closeCountry}
                  title={lang === 'es' ? 'Quitar filtro' : 'Remove filter'}
                ></i>
              </div>
            )}

            {selectedRegion && (
              <div
                className="d-inline-flex align-items-center gap-2 bg-white border border-secondary-subtle px-3 py-1 rounded-pill shadow-xs"
                style={{ fontSize: '0.825rem' }}
              >
                <i className="fa fa-map text-secondary"></i>
                <span className="text-secondary small">
                  {lang === 'es' ? 'Región:' : 'Region:'}
                </span>
                <strong className="text-dark">{selectedRegion.label}</strong>
                <i
                  className="fa fa-times text-muted cursor-pointer ms-1 hover-danger"
                  onClick={closeRegion}
                  title={lang === 'es' ? 'Quitar filtro' : 'Remove filter'}
                ></i>
              </div>
            )}

            {selectedCity && (
              <div
                className="d-inline-flex align-items-center gap-2 bg-white border border-secondary-subtle px-3 py-1 rounded-pill shadow-xs"
                style={{ fontSize: '0.825rem' }}
              >
                <i className="fa fa-map-marker text-secondary"></i>
                <span className="text-secondary small">{lang === 'es' ? 'Comuna:' : 'City:'}</span>
                <strong className="text-dark">{selectedCity.label}</strong>
                <i
                  className="fa fa-times text-muted cursor-pointer ms-1 hover-danger"
                  onClick={closeCity}
                  title={lang === 'es' ? 'Quitar filtro' : 'Remove filter'}
                ></i>
              </div>
            )}
          </>
        )}

        {/* Clear All Link (available whether open or closed if any filter is active) */}
        {activeFiltersCount > 0 && (
          <button
            type="button"
            className="btn btn-link text-muted text-decoration-none small p-0 ms-1 hover-text-dark"
            onClick={clearAllFilters}
          >
            <i className="fa fa-undo me-1"></i>
            <span>{lang === 'es' ? 'Limpiar filtros' : 'Clear filters'}</span>
          </button>
        )}
      </div>

      {/* Selectors Panel (Only when icon is clicked) */}
      {showMenu && (
        <div className="filters-dropdown-panel pt-2 pb-3 mb-2">
          <div className="row g-3 g-md-4">
            {/* Country Selector */}
            <div className="col-12 col-sm-6 col-md-4">
              <label
                htmlFor="country"
                className="form-label small fw-semibold text-secondary text-uppercase mb-2 d-flex align-items-center gap-1.5"
                style={{ fontSize: '0.78rem', letterSpacing: '0.5px' }}
              >
                <i className="fa fa-globe text-secondary"></i>
                <span>{lang === 'es' ? 'País' : 'Country'}</span>
              </label>
              <Select
                id="country"
                name="country"
                options={countriesOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                isClearable={true}
                placeholder={lang === 'es' ? 'Seleccionar país...' : 'Select country...'}
                styles={customSelectStyles}
              />
            </div>

            {/* Region Selector */}
            <div className="col-12 col-sm-6 col-md-4">
              <label
                htmlFor="region"
                className="form-label small fw-semibold text-secondary text-uppercase mb-2 d-flex align-items-center gap-1.5"
                style={{ fontSize: '0.78rem', letterSpacing: '0.5px' }}
              >
                <i className="fa fa-map text-secondary"></i>
                <span>{texts.region || 'Región'}</span>
              </label>
              <Select
                id="region"
                name="region"
                options={regionsOptions}
                value={selectedRegion}
                onChange={handleRegionChange}
                isClearable={true}
                placeholder={lang === 'es' ? 'Seleccionar región...' : 'Select region...'}
                styles={customSelectStyles}
              />
            </div>

            {/* City / Comuna Selector */}
            <div className="col-12 col-sm-6 col-md-4">
              <label
                htmlFor="city"
                className="form-label small fw-semibold text-secondary text-uppercase mb-2 d-flex align-items-center gap-1.5"
                style={{ fontSize: '0.78rem', letterSpacing: '0.5px' }}
              >
                <i className="fa fa-map-marker text-secondary"></i>
                <span>{texts.comuna || 'Comuna'}</span>
              </label>
              <Select
                id="city"
                name="city"
                options={citiesOptions}
                value={selectedCity}
                onChange={handleCiudadChange}
                isClearable={true}
                placeholder={lang === 'es' ? 'Seleccionar comuna...' : 'Select city...'}
                styles={customSelectStyles}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FiltersHome;
